import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
} from 'react';
import {
  Case,
  defaultCase,
  useCases,
} from '.';
import { WithdrawalReason, WithdrawReasonType } from '../../api/useAccountApi';
import { Account, defaultAccount } from '../account';

export type CasesContextType = {
  cases: Case[],
  setCasesData: Dispatch<SetStateAction<Case[]>>,
  fetchCases(): Promise<void>,
  createCase(): Promise<Account>,
  updateCase(data: Partial<Case>, id: string): Promise<void>,
  updateBrokerOwner(id: string, ownerId: string, ownerName: string | null): Promise<Case>,
  deleteCase(id: string): Promise<void>,
  getWithdrawalReasons(): Promise<WithdrawalReason[]>,
  withdrawCase(id: string, withdrawReason: WithdrawReasonType): Promise<Case | string>,
  reopenCase(id: string): Promise<Case | string>,
}

const defaultCasesContext = {
  cases: [],
  setCasesData: () => {},
  fetchCases: async () => {},
  createCase: async () => defaultAccount,
  updateCase: async () => {},
  updateBrokerOwner: async () => defaultCase,
  deleteCase: async () => {},
  getWithdrawalReasons: async () => [],
  withdrawCase: async () => defaultCase,
  reopenCase: async () => defaultCase,
};

export const CasesContext = createContext<CasesContextType>(defaultCasesContext);

export function useCasesContext() {
  return useContext(CasesContext);
}

export function CasesProvider({ children }: PropsWithChildren) {
  const cases = useCases();

  const contextValue = useMemo(() => ({ ...cases }), [cases]);

  return (
    <CasesContext.Provider value={contextValue}>
      {children}
    </CasesContext.Provider>
  );
}
